import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  DollarSign,
  User,
  FileText,
  Activity,
  MessageSquare,
  Edit,
  Save,
  X,
  Building2,
  MapPin,
  CreditCard,
  TrendingUp,
  History,
  Plus,
} from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import type { PromiseToPay } from '@/types/collections';

interface PromiseDetailsDrawerProps {
  promise: PromiseToPay | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EditablePromiseData {
  promiseDate: Date;
  promisedAmount: number;
  status: PromiseToPay['status'];
  confirmationMethod: PromiseToPay['confirmationMethod'];
  notes: string;
}

interface PromiseNote {
  id: string;
  content: string;
  createdBy: string;
  createdAt: Date;
  type: 'general' | 'call' | 'email' | 'system';
}

interface PromiseAction {
  id: string;
  type: 'email' | 'call' | 'task' | 'payment' | 'status_change';
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
}

export const PromiseDetailsDrawer: React.FC<PromiseDetailsDrawerProps> = ({ promise, open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<EditablePromiseData | null>(null);
  const [newNote, setNewNote] = useState('');

  // Mock data for demonstration
  const mockNotes: PromiseNote[] = promise
    ? [
        {
          id: '1',
          content: promise.notes || 'Initial promise recorded via phone call. Customer confirmed payment date.',
          createdBy: promise.createdBy,
          createdAt: promise.createdAt,
          type: 'general',
        },
        {
          id: '2',
          content: 'Follow-up call made. Customer reconfirmed commitment to pay on promised date.',
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          type: 'call',
        },
        {
          id: '3',
          content: 'Reminder email sent to customer with payment details and invoice copies.',
          createdBy: 'System',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          type: 'email',
        },
      ]
    : [];

  const mockActions: PromiseAction[] = promise
    ? [
        {
          id: '1',
          type: 'status_change',
          description: 'Promise status changed to Active',
          createdBy: promise.createdBy,
          createdAt: promise.createdAt,
        },
        {
          id: '2',
          type: 'call',
          description: 'Outbound call made to customer',
          outcome: 'Customer confirmed payment commitment',
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        },
        {
          id: '3',
          type: 'email',
          description: 'Payment reminder email sent',
          outcome: 'Email delivered successfully',
          createdBy: 'System',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
      ]
    : [];

  // Initialize edit data when entering edit mode
  const handleEnterEditMode = () => {
    if (!promise) return;
    setEditedData({
      promiseDate: promise.promiseDate,
      promisedAmount: promise.promisedAmount,
      status: promise.status,
      confirmationMethod: promise.confirmationMethod,
      notes: promise.notes,
    });
    setIsEditMode(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
  };

  // Save changes
  const handleSaveChanges = () => {
    if (!editedData || !promise) return;

    // Validation
    if (editedData.promisedAmount <= 0) {
      toast.error('Promised amount must be greater than 0');
      return;
    }

    if (!editedData.promiseDate) {
      toast.error('Promise date is required');
      return;
    }

    // In a real application, this would call an API
    console.log('Saving changes:', editedData);
    toast.success('Promise details updated successfully');
    setIsEditMode(false);
    setEditedData(null);
  };

  // Add new note
  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    // In a real application, this would call an API
    console.log('Adding note:', newNote);
    toast.success('Note added successfully');
    setNewNote('');
  };

  // Get status badge
  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { className: string; icon: React.ReactNode }> = {
      Active: {
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      Broken: {
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        icon: <AlertCircle className="h-3 w-3" />,
      },
      Paid: {
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      'Partially Paid': {
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
        icon: <Clock className="h-3 w-3" />,
      },
    };
    return (
      <Badge className={`${variants[status].className} flex items-center gap-1 w-fit`}>
        {variants[status].icon}
        {status}
      </Badge>
    );
  };

  // Get confirmation method badge
  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3" />,
      Email: <Mail className="h-3 w-3" />,
      Portal: <TrendingUp className="h-3 w-3" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1 w-fit">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  // Get action icon
  const getActionIcon = (type: PromiseAction['type']) => {
    const icons = {
      email: <Mail className="h-4 w-4 text-blue-600" />,
      call: <Phone className="h-4 w-4 text-green-600" />,
      task: <Calendar className="h-4 w-4 text-purple-600" />,
      payment: <DollarSign className="h-4 w-4 text-emerald-600" />,
      status_change: <Activity className="h-4 w-4 text-amber-600" />,
    };
    return icons[type];
  };

  // Get note icon
  const getNoteIcon = (type: PromiseNote['type']) => {
    const icons = {
      general: <MessageSquare className="h-4 w-4 text-gray-600" />,
      call: <Phone className="h-4 w-4 text-green-600" />,
      email: <Mail className="h-4 w-4 text-blue-600" />,
      system: <Activity className="h-4 w-4 text-purple-600" />,
    };
    return icons[type];
  };

  if (!promise) return null;

  const displayData = isEditMode && editedData ? editedData : promise;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="flex items-center gap-2 text-2xl">
                <HandCoins className="h-6 w-6 text-primary" />
                Promise Details
              </SheetTitle>
              <SheetDescription className="mt-1">
                Payment commitment from {promise.customerName}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveChanges}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={handleEnterEditMode}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {/* Promise ID Badge */}
          <div className="mt-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
              <span className="text-sm text-muted-foreground">Promise ID:</span>
              <span className="font-mono text-lg font-semibold text-primary">{promise.id}</span>
            </div>
          </div>

          {/* Edit Mode Indicator */}
          {isEditMode && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
              <Edit className="h-4 w-4" />
              <span className="font-medium">Edit Mode Active</span>
              <span className="text-muted-foreground">- Make changes and click Save to update</span>
            </div>
          )}
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-6 pt-4 border-b">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="customer" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Customer
              </TabsTrigger>
              <TabsTrigger value="transactions" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Transactions
              </TabsTrigger>
              <TabsTrigger value="actions" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Actions
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                  {mockActions.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Notes
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                  {mockNotes.length}
                </Badge>
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            {/* Overview Tab */}
            <TabsContent value="overview" className="px-6 py-4 space-y-6">
              {/* Promise Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Promise Summary</CardTitle>
                  <CardDescription>Key details about this payment commitment</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Promise Date */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Promise Date
                    </Label>
                    {isEditMode ? (
                      <Input
                        type="date"
                        value={format(editedData!.promiseDate, 'yyyy-MM-dd')}
                        onChange={(e) =>
                          setEditedData({ ...editedData!, promiseDate: new Date(e.target.value) })
                        }
                        className="w-48"
                      />
                    ) : (
                      <span className="font-medium">{format(displayData.promiseDate, 'MMM dd, yyyy')}</span>
                    )}
                  </div>

                  <Separator />

                  {/* Promised Amount */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Promised Amount
                    </Label>
                    {isEditMode ? (
                      <Input
                        type="number"
                        value={editedData!.promisedAmount}
                        onChange={(e) =>
                          setEditedData({ ...editedData!, promisedAmount: parseFloat(e.target.value) || 0 })
                        }
                        className="w-48"
                        min="0"
                        step="0.01"
                      />
                    ) : (
                      <span className="font-medium text-lg">${displayData.promisedAmount.toLocaleString()}</span>
                    )}
                  </div>

                  <Separator />

                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Status
                    </Label>
                    {isEditMode ? (
                      <Select
                        value={editedData!.status}
                        onValueChange={(value) =>
                          setEditedData({ ...editedData!, status: value as PromiseToPay['status'] })
                        }
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Broken">Broken</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      getStatusBadge(displayData.status)
                    )}
                  </div>

                  <Separator />

                  {/* Confirmation Method */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Confirmation Method
                    </Label>
                    {isEditMode ? (
                      <Select
                        value={editedData!.confirmationMethod}
                        onValueChange={(value) =>
                          setEditedData({
                            ...editedData!,
                            confirmationMethod: value as PromiseToPay['confirmationMethod'],
                          })
                        }
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="Portal">Portal</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      getConfirmationMethodBadge(displayData.confirmationMethod)
                    )}
                  </div>

                  <Separator />

                  {/* Days Overdue */}
                  {promise.daysOverdue && promise.daysOverdue > 0 && (
                    <>
                      <div className="flex items-center justify-between">
                        <Label className="text-muted-foreground flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Days Overdue
                        </Label>
                        <Badge variant="destructive" className="text-base px-3 py-1">
                          {promise.daysOverdue} days
                        </Badge>
                      </div>
                      <Separator />
                    </>
                  )}

                  {/* Created By */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Created By
                    </Label>
                    <span className="font-medium">{promise.createdBy}</span>
                  </div>

                  <Separator />

                  {/* Created At */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Created At
                    </Label>
                    <span className="font-medium">{format(promise.createdAt, 'MMM dd, yyyy HH:mm')}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Risk Indicators */}
              {(promise.isAtRisk || (promise.brokenPromisesCount && promise.brokenPromisesCount > 0)) && (
                <Card className="border-amber-200 dark:border-amber-800">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-amber-600 dark:text-amber-400">
                      <AlertCircle className="h-5 w-5" />
                      Risk Indicators
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {promise.isAtRisk && (
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="destructive">High Risk</Badge>
                        <span className="text-muted-foreground">
                          Customer has a history of broken payment promises
                        </span>
                      </div>
                    )}
                    {promise.brokenPromisesCount && promise.brokenPromisesCount > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">{promise.brokenPromisesCount} Broken Promises</Badge>
                        <span className="text-muted-foreground">Previous payment commitments not honored</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Notes Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  {isEditMode ? (
                    <Textarea
                      value={editedData!.notes}
                      onChange={(e) => setEditedData({ ...editedData!, notes: e.target.value })}
                      placeholder="Add notes about this promise..."
                      rows={4}
                      className="resize-none"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {displayData.notes || 'No notes available'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Customer Tab */}
            <TabsContent value="customer" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Customer Information
                  </CardTitle>
                  <CardDescription>Contact details and account information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Customer Name */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Customer Name
                    </Label>
                    <span className="font-medium">{promise.customerName}</span>
                  </div>

                  <Separator />

                  {/* Account Number */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Account Number
                    </Label>
                    <span className="font-mono font-medium">{promise.customerAccountNumber}</span>
                  </div>

                  <Separator />

                  {/* Email */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Address
                    </Label>
                    <a
                      href={`mailto:${promise.customerEmail}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {promise.customerEmail}
                    </a>
                  </div>

                  <Separator />

                  {/* Phone */}
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number
                    </Label>
                    <a href={`tel:${promise.customerPhone}`} className="font-medium text-primary hover:underline">
                      {promise.customerPhone}
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                  <CardDescription>Contact customer or create follow-up tasks</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="justify-start">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  <Button variant="outline" className="justify-start">
                    <Phone className="h-4 w-4 mr-2" />
                    Log Call
                  </Button>
                  <Button variant="outline" className="justify-start">
                    <Calendar className="h-4 w-4 mr-2" />
                    Create Task
                  </Button>
                  <Button variant="outline" className="justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    View Account
                  </Button>
                </CardContent>
              </Card>

              {/* Customer History */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Customer History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Total Promises Made</span>
                    <span className="font-medium">
                      {(promise.brokenPromisesCount || 0) + 1} (including current)
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Broken Promises</span>
                    <span className="font-medium text-red-600">{promise.brokenPromisesCount || 0}</span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Promise Success Rate</span>
                    <span className="font-medium text-green-600">
                      {promise.brokenPromisesCount
                        ? Math.round((1 / (promise.brokenPromisesCount + 1)) * 100)
                        : 100}
                      %
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Invoice Allocations
                  </CardTitle>
                  <CardDescription>
                    Invoices included in this promise ({promise.invoices.length} total)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice Number</TableHead>
                          <TableHead>Invoice Date</TableHead>
                          <TableHead className="text-right">Original Amount</TableHead>
                          <TableHead className="text-right">Promised Amount</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {promise.invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium font-mono">{invoice.invoiceNumber}</TableCell>
                            <TableCell>{format(invoice.invoiceDate, 'MMM dd, yyyy')}</TableCell>
                            <TableCell className="text-right">
                              ${invoice.originalAmount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              ${invoice.promisedAmount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              ${invoice.balanceRemaining.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  invoice.currentStatus === 'Paid'
                                    ? 'default'
                                    : invoice.currentStatus === 'Partially Paid'
                                      ? 'secondary'
                                      : 'outline'
                                }
                                className={
                                  invoice.currentStatus === 'Paid'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : invoice.currentStatus === 'Partially Paid'
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                                      : ''
                                }
                              >
                                {invoice.currentStatus}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Transaction Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Invoices</span>
                    <span className="font-medium">{promise.invoices.length}</span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Original Amount</span>
                    <span className="font-medium">
                      ${promise.invoices.reduce((sum, inv) => sum + inv.originalAmount, 0).toLocaleString()}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Promised Amount</span>
                    <span className="font-medium text-lg text-primary">
                      ${promise.invoices.reduce((sum, inv) => sum + inv.promisedAmount, 0).toLocaleString()}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Balance Remaining</span>
                    <span className="font-medium">
                      ${promise.invoices.reduce((sum, inv) => sum + inv.balanceRemaining, 0).toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Actions Tab */}
            <TabsContent value="actions" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Action History
                  </CardTitle>
                  <CardDescription>All activities related to this promise</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockActions.map((action, index) => (
                      <div key={action.id}>
                        <div className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted border">
                              {getActionIcon(action.type)}
                            </div>
                            {index < mockActions.length - 1 && (
                              <div className="h-full w-px bg-border mt-2 mb-2" />
                            )}
                          </div>
                          <div className="flex-1 pb-6">
                            <div className="flex items-start justify-between mb-1">
                              <p className="font-medium text-sm">{action.description}</p>
                              <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                                {format(action.createdAt, 'MMM dd, HH:mm')}
                              </span>
                            </div>
                            {action.outcome && (
                              <p className="text-sm text-muted-foreground mt-1">{action.outcome}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">By {action.createdBy}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Available Actions</CardTitle>
                  <CardDescription>Perform actions on this promise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Payment Reminder Email
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Phone className="h-4 w-4 mr-2" />
                    Log Follow-up Call
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Follow-up Task
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Record Payment Received
                  </Button>
                  <Separator />
                  <Button className="w-full justify-start" variant="outline">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Mark Promise as Broken
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="px-6 py-4 space-y-6">
              {/* Add New Note */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Add New Note
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder="Enter your note here..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                  <Button onClick={handleAddNote} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Note
                  </Button>
                </CardContent>
              </Card>

              {/* Notes List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    All Notes ({mockNotes.length})
                  </CardTitle>
                  <CardDescription>Chronological history of notes and comments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockNotes.map((note, index) => (
                      <div key={note.id}>
                        <div className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted border">
                              {getNoteIcon(note.type)}
                            </div>
                            {index < mockNotes.length - 1 && <div className="h-full w-px bg-border mt-2 mb-2" />}
                          </div>
                          <div className="flex-1 pb-6">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{note.createdBy}</span>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {note.type}
                                </Badge>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                                {format(note.createdAt, 'MMM dd, yyyy HH:mm')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{note.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
